import { getSelectableGradingScale } from 'lib/api'
import { useEffect, useState } from 'react'
import { GradingScaleByCompany } from 'shared'

// The scale is a hundred-odd rows that only change when someone edits them from the backoffice, so it
// is fetched once per page load and shared by every place asking for it. Without this, the three points
// where a seller declares a card graded would each fetch the same list, and the grading panel would
// refetch it every time a row is opened.
let cache: GradingScaleByCompany | undefined
let inFlight: Promise<GradingScaleByCompany> | undefined

// The stock fetch is one pass over every game and reads every comment against the company's own scale, so
// whoever starts it has to know the scale is here. Exported beside the hook because that caller is an event
// handler and not a render. A failed request resolves too: the import goes on with the shape check alone, which
// beats not going on at all.
export const awaitGradingScale = async (): Promise<void> => {
  if (cache !== undefined) return

  inFlight = inFlight ?? getSelectableGradingScale()

  try {
    cache = await inFlight
  } catch {
    inFlight = undefined
  }
}

export const useGradingScale = (): {
  gradingScale: GradingScaleByCompany
  gradesFor: (gradingCompany: string | undefined) => string[]
  loading: boolean
  error: boolean
} => {
  const [gradingScale, setGradingScale] = useState<GradingScaleByCompany>(cache ?? {})
  const [loading, setLoading] = useState(cache === undefined)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (cache !== undefined) return

    let active = true
    // Two components mounting together would otherwise fire two identical requests: the second one
    // waits on the first instead.
    inFlight = inFlight ?? getSelectableGradingScale()

    inFlight
      .then((scale) => {
        cache = scale
        if (active) setGradingScale(scale)
      })
      .catch(() => {
        // A failure is not cached: opening the panel again is allowed to try once more.
        inFlight = undefined
        if (active) setError(true)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [])

  // An unknown company yields no options rather than every grade there is: offering a PSA 9.5, which
  // PSA does not award, is exactly what this list exists to prevent.
  const gradesFor = (gradingCompany: string | undefined): string[] =>
    gradingCompany ? [...(gradingScale[gradingCompany] ?? [])].reverse() : []

  return { gradingScale, gradesFor, loading, error }
}
